'use client';

import { type MouseEvent, useCallback, useMemo, useState } from 'react';

import type {
  TableOnSortChangeStateType,
  TableRootContextInterface,
  TableRootPropsInterface,
} from '../types/table-root.types';

const DEFAULT_SORT_DIRECTION_ORDER: NonNullable<TableRootPropsInterface['sortDirectionOrder']> = [
  'ascending',
  'descending',
];

/**
 * Narrowed input type — only the sort-related slice of `TableRootPropsInterface`.
 * Lets the consumer (`Table.Root`) keep its remaining `rest` to spread directly
 * onto the `<table>` element without round-tripping through the hook.
 */
type UseSortableTablePropsType = Pick<
  TableRootPropsInterface,
  | 'defaultSortBy'
  | 'defaultSortDirection'
  | 'onSortChange'
  | 'sortBy'
  | 'sortDirection'
  | 'sortDirectionOrder'
> & {
  isControlled: boolean;
};

/**
 * A hook that implements controlled and uncontrolled sorting behavior.
 *
 * Manages the sorting state and transitions between sort directions for table columns.
 * Supports both controlled mode (when sortBy/sortDirection props are provided) and
 * uncontrolled mode (using internal state with defaultSort props).
 */
export const useSortableTable = (props: UseSortableTablePropsType): TableRootContextInterface => {
  const {
    defaultSortBy,
    defaultSortDirection,
    isControlled,
    sortBy: controlledSortBy,
    sortDirection: controlledSortDirection,
    onSortChange: setControlledSortBy,
    sortDirectionOrder = DEFAULT_SORT_DIRECTION_ORDER,
  } = props;

  const [uncontrolledSortBy, setUncontrolledSortBy] = useState(defaultSortBy);
  const [uncontrolledSortDirection, setUncontrolledSortDirection] = useState(defaultSortDirection);

  const currentSortState = useMemo(
    () => ({
      sortBy: isControlled ? controlledSortBy : uncontrolledSortBy,
      sortDirection: isControlled ? controlledSortDirection : uncontrolledSortDirection,
    }),
    [
      controlledSortBy,
      controlledSortDirection,
      isControlled,
      uncontrolledSortBy,
      uncontrolledSortDirection,
    ],
  );

  const onSortChange = useCallback(
    (state: TableOnSortChangeStateType, event: MouseEvent<HTMLTableCellElement>) => {
      if (isControlled) {
        setControlledSortBy?.(state, event);
      } else {
        setUncontrolledSortBy(state.sortBy);
        setUncontrolledSortDirection(state.sortDirection);

        setControlledSortBy?.(state, event);
      }
    },
    [isControlled, setControlledSortBy],
  );

  return useMemo(
    () => ({
      onSortChange,
      sortBy: currentSortState.sortBy,
      sortDirection: currentSortState.sortDirection,
      sortDirectionOrder,
    }),
    [onSortChange, currentSortState, sortDirectionOrder],
  );
};
